import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import type { AjaxError } from 'rxjs';
import { signUp } from 'rest/sign-up';
import type { exitTestModeAction } from 'state/actions/login';
import { SsoTestModeBanner } from 'view/sso-test-mode-banner';
import { AnalyticsContext, useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import type { Error } from '@atlassian/help-center-common-util/errors';
import { getJiraErrorOrDefault } from '@atlassian/help-center-common-util/errors';
import { history } from '@atlassian/help-center-common-util/history';
import { search } from '@atlassian/help-center-common-util/location';
import type { AuthInfo } from '@atlassian/help-center-common-util/login/constant';
import { AuthMethodType } from '@atlassian/help-center-common-util/login/constant';
import {
    getLoginUrl,
    getLoginUrlForTestMode,
    getSsoTestModeParamFromUrl,
    isForThisJiraTenant,
    parseLoginSignupRedirectUrlParams,
} from '@atlassian/help-center-common-util/url';
import { SignUpStateless } from './sign-up-stateless';

interface SignUpFormProps {
    authInfo: AuthInfo;
    emailAddress: string;
    title: string;
    requestSummary: string | null;
    portalId: string | undefined;
    baseUrl: string;
    onExitTestMode: typeof exitTestModeAction;
}

interface State {
    isSubmitting: boolean;
    error: Error | undefined;
}

const getRedirectUrl = (baseUrl: string) => {
    const { destination, isAbsolute } = parseLoginSignupRedirectUrlParams();
    if (destination && isAbsolute && isForThisJiraTenant(destination, baseUrl)) {
        return destination;
    }

    return undefined;
};

export const SignUp = (props: SignUpFormProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const [{ isSubmitting, error }, setState] = useState<State>({ isSubmitting: false, error: undefined });
    const { authInfo, emailAddress, portalId, baseUrl } = props;
    const isPasswordAvailable = authInfo.authMethods.has(AuthMethodType.CUSTOMER_ACCOUNT_PASSWORD);
    const singleSignOnMethod = authInfo.authMethods.get(AuthMethodType.CUSTOMER_ACCOUNT_SSO);
    const isSingleSignOnAvailable = singleSignOnMethod !== undefined;
    const ssoRedirectUrl = isSingleSignOnAvailable ? singleSignOnMethod.redirectLink : undefined;
    const isSsoTestMode = getSsoTestModeParamFromUrl(new URLSearchParams(search()));

    useEffect(() => {
        if (!emailAddress) {
            history.push(
                getLoginUrl({
                    portalId: portalId ? Number.parseInt(portalId, 10) : undefined,
                    isAbsolute: false,
                })
            );
        }
    }, [portalId, emailAddress]);

    useEffect(() => {
        const redirectUrlIsMissing = isSingleSignOnAvailable && ssoRedirectUrl === undefined;
        const noMethodsAvailable = !isSingleSignOnAvailable && !isPasswordAvailable;

        if (redirectUrlIsMissing || noMethodsAvailable) {
            if (createAnalyticsEvent) {
                createAnalyticsEvent({
                    analyticsType: OPERATIONAL_EVENT_TYPE,
                    actionSubject: 'signUp',
                    action: 'failed',
                    attributes: { redirectUrlIsMissing, noMethodsAvailable },
                }).fire();
            }
        }
    }, [createAnalyticsEvent, isPasswordAvailable, isSingleSignOnAvailable, ssoRedirectUrl]);

    const onSubmitButtonClick = useCallback(async () => {
        setState({
            isSubmitting: true,
            error: undefined,
        });

        try {
            const redirectUrl = getRedirectUrl(baseUrl);
            const portalIdAsNumber = portalId === undefined ? undefined : Number(portalId);
            await signUp(emailAddress.trim(), '', portalIdAsNumber, redirectUrl);
            setState({
                isSubmitting: false,
                error: undefined,
            });
            history.push('/user/check-email', {
                portalId,
                redirectUrl,
                email: emailAddress,
            });
        } catch (exception) {
            setState({
                isSubmitting: false,
                error: getJiraErrorOrDefault(exception as AjaxError),
            });
        }
    }, [setState, emailAddress, portalId, baseUrl]);

    const onBackButtonClick = useCallback(() => {
        const loginUrl = isSsoTestMode
            ? getLoginUrlForTestMode({
                  portalId: portalId ? Number.parseInt(portalId, 10) : undefined,
                  isAbsolute: false,
              })
            : getLoginUrl({
                  portalId: portalId ? Number.parseInt(portalId, 10) : undefined,
                  isAbsolute: false,
              });
        history.push(loginUrl);
    }, [portalId, isSsoTestMode]);

    return (
        <AnalyticsContext data={{ attributes: { isSsoTestMode } }}>
            <SsoTestModeBanner />
            <SignUpStateless
                {...props}
                // @typescript-eslint upgrade to v5.61.0
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                onSubmitButtonClick={onSubmitButtonClick}
                onBackButtonClick={onBackButtonClick}
                hasSingleSignOnAvailable={isSingleSignOnAvailable}
                hasPasswordAvailable={isPasswordAvailable}
                ssoRedirectUrl={ssoRedirectUrl}
                isSubmitting={isSubmitting}
                error={error}
            />
        </AnalyticsContext>
    );
};

export default SignUp;
